/**
 * RolePermissions Value Object
 *
 * Encapsulates the business logic for constructing and validating
 * a Role's permissions.
 *
 * Business Rules:
 * - At least one permission is required when creating a role
 * - Roles from the backend may have zero permissions (reconstruction is lenient)
 * - Permissions are resolved from backend IDs (at least one match grants the permission)
 * - Special case: endSessionKitchen flag adds store-pos-accounts-auto-logout permission
 *
 * @module domains/personnel/domain/value-objects/RolePermissions
 */

import type { Permission, FrontendPermissionId } from '@personnel/domain/types/PermissionsConst';
import { PERMISSIONS } from '@personnel/domain/types/PermissionsConst';

/**
 * RolePermissions type - validated readonly array of permissions
 *
 * This is simply readonly Permission[] because branding doesn't add value here.
 * The validation happens in the factory functions, not the type itself.
 */
export type RolePermissions = readonly Permission[];

/**
 * Business rule: Minimum number of permissions required when creating a role.
 * Used by the form schema (Zod) and createNewRole factory.
 */
export const MIN_PERMISSIONS_REQUIRED = 1;

/**
 * Input data for creating RolePermissions from API
 */
export type RolePermissionsInput = {
  backendPermissionIds: string[];
  endSessionKitchen: boolean;
};

/**
 * Resolves which permissions are granted based on backend permission IDs
 *
 * Domain function that determines which permissions a role has.
 * A permission is considered granted if AT LEAST ONE of its primaryIds is present.
 * dependencyIds are ignored for matching (they're only used when writing).
 *
 * This supports gradual permission migration where new permission slugs are added
 * alongside old ones. As long as at least one primaryId matches, the permission is granted.
 *
 * @param backendPermissionIds - Array of permission IDs from backend
 * @returns Array of resolved frontend permission IDs
 *
 * @example
 * const backendIds = ['admin.canAdminGiftCerticate'];
 * const granted = resolvePermissions(backendIds);
 * // => ['org-gift-certificates'] (granted because at least one primaryId matches)
 */
export const resolvePermissions = (backendPermissionIds: string[]): FrontendPermissionId[] => {
  const backendSet = new Set(backendPermissionIds);
  const grantedPermissions: FrontendPermissionId[] = [];

  for (const [permissionId, permission] of Object.entries(PERMISSIONS)) {
    // Check if AT LEAST ONE primaryId for this permission is present
    // dependencyIds are NOT used for matching
    const primaryIds = [...permission.primaryIds];
    const hasAnyPrimaryMatch = primaryIds.some((id) => backendSet.has(id));

    if (hasAnyPrimaryMatch) {
      grantedPermissions.push(permissionId as FrontendPermissionId);
    }
  }

  return grantedPermissions;
};

/**
 * Factory: Creates RolePermissions from backend data
 *
 * Applies business rules:
 * 1. Resolves which permissions are granted from backend IDs
 * 2. If endSessionKitchen is true, adds the auto-logout permission
 * 3. Validates that at least one permission exists
 *
 * @param input - Backend permission data
 * @returns RolePermissions value object
 * @throws {Error} If no permissions are resolved (business rule violation)
 *
 * @example
 * const rolePermissions = createRolePermissions({
 *   backendPermissionIds: ['admin.canAdminGiftCerticate', 'backoffice.access'],
 *   endSessionKitchen: true
 * });
 */
export const createRolePermissions = (input: RolePermissionsInput): RolePermissions => {
  // Step 1: Resolve which permissions are granted
  const grantedPermissionIds = resolvePermissions(input.backendPermissionIds);

  // Step 2: Convert permission IDs to full Permission objects
  const permissions: Permission[] = grantedPermissionIds.map((permissionId) => PERMISSIONS[permissionId]);

  // Step 3: Business Rule - Add endSessionKitchen permission if flag is true
  if (input.endSessionKitchen) {
    const endSessionPermission = PERMISSIONS['store-pos-accounts-auto-logout'];
    if (endSessionPermission && !permissions.includes(endSessionPermission)) {
      permissions.push(endSessionPermission);
    }
  }

  return permissions as RolePermissions;
};
